import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
} from '@angular/core';

import { FuentesFoto } from '../../core/models/propiedad.model';
import { PlaceholderImagen } from '../placeholder-imagen/placeholder-imagen';

/**
 * Foto de propiedad servida con `<picture>` (FP-28): AVIF para el navegador
 * que lo soporte y WebP para el resto. El backend genera los dos formatos en
 * tres tamaños; elegir la variante es del consumidor (ver `fuentesFoto()`).
 *
 * Portado de `PropertyPhotoComponent` del backoffice.
 *
 * ## Cuándo muestra el visual de "sin imagen"
 *
 * `fuentes` acepta `null` y el `<img>` engancha `(error)`, así que cubre los
 * dos casos en los que no hay foto que mostrar:
 *
 * - **No hay foto** (`fuentes` en `null`).
 * - **La foto no cargó**: 404, objeto borrado del bucket, red caída. Sin esto
 *   queda el icono de imagen rota del navegador.
 *
 * ## Qué variante usar
 *
 * `ladoMax` limita el lado MAYOR y el encuadre lo hace `object-fit: cover`,
 * así que el ancho útil es menor al nominal:
 *
 * | Slot                     | Variante      |
 * |--------------------------|---------------|
 * | Tarjeta del listado      | `card` (800)  |
 * | Tarjeta del pin del mapa | `card` (800)  |
 * | Foto principal de ficha  | `card` (800)  |
 * | Miniaturas de la ficha   | `thumb` (400) |
 *
 * `full` (1920) queda para cuando la foto se vea a pantalla completa.
 */
@Component({
  selector: 'app-foto-propiedad',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PlaceholderImagen],
  template: `
    @if (fotoAMostrar(); as foto) {
      <picture>
        @if (foto.avif; as avif) {
          <source [srcset]="avif" type="image/avif" />
        }
        <img
          [src]="foto.src"
          [alt]="alt()"
          [attr.loading]="loading()"
          [attr.fetchpriority]="prioridad()"
          [style.object-fit]="ajuste()"
          decoding="async"
          (error)="falloCarga.set(true)"
        />
      </picture>
    } @else if (placeholder() === 'marca') {
      <!-- alt vacío a propósito: la imagen no dice nada de la propiedad, sólo
           llena el hueco. La dirección y el precio ya los lee el lector de
           pantalla del resto de la tarjeta. -->
      <img src="/logos/thumb.png" alt="" loading="lazy" decoding="async" />
    } @else {
      <app-placeholder-imagen />
    }
  `,
  styles: `
    :host {
      display: block;
      overflow: hidden;
    }

    picture,
    img {
      display: block;
      width: 100%;
      height: 100%;
    }

    img {
      object-fit: cover;
    }
  `,
})
export class FotoPropiedad {
  /** `null` cuando no hay foto: se muestra el visual de "sin imagen". */
  readonly fuentes = input.required<FuentesFoto | null>();
  /** Obligatorio a propósito, para que no se olvide en un consumidor nuevo. */
  readonly alt = input.required<string>();

  /**
   * Qué mostrar cuando no hay foto que renderizar. `neutro` es el visual del
   * DS; `marca` es la imagen de Phindi, que usa la tarjeta para que la grilla
   * del buscador no quede con huecos.
   *
   * Vive acá y no en el consumidor porque cubre los DOS casos sin foto: la
   * propiedad sin imágenes y la foto que falla al cargar. Ese segundo caso lo
   * detecta este componente por dentro (`falloCarga`), así que un `@if` en la
   * tarjeta se lo perdería y una foto rota seguiría mostrando el placeholder.
   */
  readonly placeholder = input<'neutro' | 'marca'>('neutro');

  /**
   * `eager` para la foto que ya está en el viewport inicial (la principal de
   * la ficha): es candidata a LCP y diferirla lo empeora. `lazy` —el default—
   * para tarjetas del listado y miniaturas.
   */
  readonly loading = input<'lazy' | 'eager'>('lazy');

  /**
   * Cómo entra la foto en su caja. `cover` —el default— llena y recorta: es lo
   * que quieren las tarjetas y la galería, donde el slot tiene una forma fija.
   * `contain` la muestra entera: sirve para verla a pantalla completa, donde
   * recortar sería justamente lo que no se quiere.
   */
  readonly ajuste = input<'cover' | 'contain'>('cover');

  /**
   * Se deriva de `loading` en vez de ser un input aparte para que no queden
   * desalineados: la foto que se carga `eager` es la que se quiere priorizar
   * en la cola de red. `null` omite el atributo.
   */
  protected readonly prioridad = computed<'high' | null>(() =>
    this.loading() === 'eager' ? 'high' : null,
  );

  /**
   * La foto falló al cargar. Es `linkedSignal` y no `signal` para que se
   * resetee solo cada vez que cambia `fuentes`: si no, al pasar de foto en el
   * carrusel una rota dejaba el placeholder pegado en las siguientes.
   */
  protected readonly falloCarga = linkedSignal<FuentesFoto | null, boolean>({
    source: this.fuentes,
    computation: () => false,
  });

  /** Foto a renderizar, o `null` si no hay o no cargó. */
  protected readonly fotoAMostrar = computed<FuentesFoto | null>(() =>
    this.falloCarga() ? null : this.fuentes(),
  );
}
